import React, { Component, useCallback, useEffect, useMemo, useState } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { BRAND_COLOR } from '../../constants'
import { useAuthContext } from '../../contexts/AuthContext'
import { homePopupService } from '../../services/homePopup'
import type { HomePopup as HomePopupItem } from '../../types/homePopup'

const DISMISS_PREFIX = 'home-popup-dismissed'

function dismissKey(userId: string, popup: HomePopupItem): string {
  return `${DISMISS_PREFIX}:${userId}:${popup.id}:${popup.updated_at}`
}

function isDismissed(userId: string, popup: HomePopupItem): boolean {
  try {
    return localStorage.getItem(dismissKey(userId, popup)) === '1'
  } catch {
    return false
  }
}

function markDismissed(userId: string, popup: HomePopupItem): void {
  try {
    localStorage.setItem(dismissKey(userId, popup), '1')
  } catch {
    // ignore quota / private mode
  }
}

class HomePopupErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('HomePopup falhou e foi isolado da home:', error, info)
  }

  render() {
    if (this.state.hasError) return null
    return this.props.children
  }
}

const HomePopupContent: React.FC = () => {
  const { user } = useAuthContext()
  const [queue, setQueue] = useState<HomePopupItem[]>([])
  const [dontShowAgain, setDontShowAgain] = useState(false)

  useEffect(() => {
    if (!user?.id) return

    let cancelled = false
    ;(async () => {
      try {
        const active = await homePopupService.listActive()
        if (cancelled) return
        const pending = (Array.isArray(active) ? active : []).filter(
          (item) =>
            item?.id &&
            item?.title &&
            item?.body &&
            item?.updated_at &&
            !isDismissed(user.id, item)
        )
        setQueue(pending)
      } catch (err) {
        // Tabela ausente / RLS / rede: não derruba a home
        console.error('Erro ao carregar avisos da home:', err)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [user?.id])

  const current = useMemo(() => queue[0] ?? null, [queue])

  const handleClose = useCallback(() => {
    if (!current || !user?.id) {
      setQueue([])
      return
    }
    if (dontShowAgain) {
      markDismissed(user.id, current)
    }
    setDontShowAgain(false)
    setQueue((prev) => prev.slice(1))
  }, [current, dontShowAgain, user?.id])

  if (!current) return null

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="home-popup-title"
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col border-2 overflow-hidden"
        style={{ borderColor: BRAND_COLOR }}
      >
        <div
          className="flex items-start justify-between gap-4 p-6"
          style={{ backgroundColor: BRAND_COLOR }}
        >
          <h2
            id="home-popup-title"
            className="text-xl font-semibold text-white"
          >
            {current.title}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Fechar"
            className="inline-flex items-center justify-center shrink-0 rounded-lg p-1.5 text-white/90 hover:bg-white/15"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
            {current.body}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-6 border-t border-gray-200 dark:border-gray-700">
          <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-gray-600 dark:text-gray-400">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Não mostrar novamente
          </label>
          <button
            type="button"
            onClick={handleClose}
            style={{ backgroundColor: BRAND_COLOR }}
            className="inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold text-white"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  )
}

const HomePopup: React.FC = () => (
  <HomePopupErrorBoundary>
    <HomePopupContent />
  </HomePopupErrorBoundary>
)

export default HomePopup
